import TopBar from 'components/AugmentedReality/Structure/TopBar';
import BottomBar from 'components/AugmentedReality/PhotoEditor/BottomBar';
import {
  SCPhotoEditorContainer,
  SCOverlayImage,
  SCVideo,
  SCStickerContainer as SCStickerListContainer,
  SCStickerScroll,
  SCSticker,
  SCPhotoContainer,
  SCFrameSliceImg,
  SCTopStack,
} from 'components/AugmentedReality/PhotoEditor/styled'
import useCamera from 'components/AugmentedReality/hooks/useCamera';
import useStickers from 'components/AugmentedReality/hooks/useStickers';
import useDeviceOrientation from 'components/AugmentedReality/hooks/useDeviceOrientation';
import Sticker from 'components/AugmentedReality/PhotoEditor/Sticker';
import { AugmentedRealityType } from '../../../types/augmentedReality';
import React, { useRef, useEffect } from 'react'
import { DEFAULT_STICKERS } from 'utils/consts';
import { useTranslation } from 'react-i18next';

const getWatermarkStyle = (position: number): React.CSSProperties => {
  const col = position % 3           // 0=left  1=center  2=right
  const row = Math.floor(position / 3) // 0=top   1=middle  2=bottom
  const transforms: string[] = []
  const style: React.CSSProperties = {
    position: 'absolute',
    maxWidth: '40%',
    maxHeight: '30%',
    objectFit: 'contain',
    zIndex: 3,
    pointerEvents: 'none',
  }
  if (row === 0) {
    style.top = 0
  }
  else if (row === 2) {
    style.bottom = 0
  }
  else {
    style.top = '50%'; transforms.push('translateY(-50%)')
  }

  if (col === 0) {
    style.left = 0
  }
  else if (col === 2) {
    style.right = 0
  }
  else {
    style.left = '50%'; transforms.push('translateX(-50%)')
  }

  if (transforms.length) {
    style.transform = transforms.join(' ')
  }
  return style
}

interface PhotoEditorFrameProps {
  ra: AugmentedRealityType;
  camera: ReturnType<typeof useCamera>;
  download: boolean;
  onDownload: (state: boolean) => void;
  onPrevious: () => void;
  onCancel: () => void;
  onGoLive: () => void;
  capturedPhoto: string;
  currentOverlay: string;
  overlayTransform: { x: number; y: number; scale: number };
}

const PhotoEditorFrame = ({ ra, camera, download, onDownload, onPrevious, onCancel, onGoLive, capturedPhoto, currentOverlay, overlayTransform }: PhotoEditorFrameProps) => {
  const { t } = useTranslation();
  const { addSticker, stickers, selectedStickerId, updateSticker, deleteSticker, selectSticker, deselectSticker } = useStickers();
  const photoRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLImageElement>(null);
  const frameRef = useRef<HTMLImageElement>(null);
  const { isPortrait } = useDeviceOrientation();
  const frameUrl = isPortrait ? ra.stickers?.framePortrait : ra.stickers?.frameLandscape;

  const handleStickerClick = (stickerUrl: string) => {
    addSticker(stickerUrl);
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      deselectSticker();
    }
  };

  return (
    <SCPhotoEditorContainer>
      <SCTopStack>
        <TopBar label={t('augmentedReality.photoEditor.title')}/>

        {ra.stickers?.use !== 'none' && (
          <SCStickerListContainer>
            <SCStickerScroll>
              {(ra.stickers?.use === 'default' ? DEFAULT_STICKERS : ra.stickers?.items || []).map(
                (stickerUrl, index) => (
                  <SCSticker
                    crossOrigin="anonymous"
                    key={index}
                    src={stickerUrl}
                    alt={`Sticker ${index + 1}`}
                    onClick={() => handleStickerClick(stickerUrl)}
                  />
                )
              )}
            </SCStickerScroll>
          </SCStickerListContainer>
        )}
      </SCTopStack>

      <SCPhotoContainer ref={photoRef} onClick={handleBackgroundClick}>
        <SCVideo as="img" src={capturedPhoto} alt="Captured photo" />

        {ra.stickers?.hideOverlay === false && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              transform: `translate(${overlayTransform.x}px, ${overlayTransform.y}px) scale(${overlayTransform.scale})`,
              transformOrigin: '0px 0px',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            <SCOverlayImage ref={overlayRef} crossOrigin="anonymous" src={currentOverlay} alt="filter overlay" $size={300} />
          </div>
        )}

        {stickers.map((sticker) => (
          <Sticker
            key={sticker.id}
            sticker={sticker}
            onUpdate={updateSticker}
            onDelete={deleteSticker}
            onSelect={selectSticker}
            isSelected={sticker.id === selectedStickerId}
          />
        ))}

        {frameUrl && (
          <>
            <SCFrameSliceImg ref={frameRef} crossOrigin="anonymous" src={frameUrl} alt="frame-left" style={{ left: 0 }} />
          </>
        )}

        {!ra.watermark?.noUse && ra.watermark?.image && (
          <img crossOrigin="anonymous" src={ra.watermark.image} alt="watermark" style={getWatermarkStyle(ra.watermark.position)} />
        )}
      </SCPhotoContainer>

      <BottomBar
        camera={camera}
        download={download}
        onDownload={onDownload}
        currentOverlay={currentOverlay}
        onPrevious={onPrevious}
        onCancel={onCancel}
        onGoLive={onGoLive}
        capturedPhoto={capturedPhoto}
        stickers={stickers}
        photoRef={photoRef}
        overlayRef={overlayRef}
        hideOverlay={ra.stickers?.hideOverlay || false}
        frameRef={frameRef}
        frameUrl={frameUrl}
        watermark={ra.watermark}
      />
    </SCPhotoEditorContainer>
  )
};

export default PhotoEditorFrame;
